import type { PricingGridRow } from "./types"

export const MAX_TIERS_PER_GROUP = 10

export const MAX_SURCHARGES_PER_ROW = 3

import type { PriceChangeItem } from "@/graphql/codegen/graphql"

const EMPTY_ITEM: PriceChangeItem = {
  id: "",
  priceChangeItemId: "",
  customerId: "",
  productId: "",
  shipFromWarehouseAreaId: "",
  tiers: [],
  customer: null,
  shipTo: null,
  shipToLocationId: null,
  freightTerms: null,
  last12MonthsQty: null,
  warehouseArea: null,
  billBack: null,
  previousBillBack: null,
}

export const BLANK_PRICING_TIER: Partial<PricingGridRow> = {
  qty: 0,
  newPrice: null,
  newProfit: null,
  newMargin: null,
  surcharge1Price: null,
  surcharge1Description: null,
  surcharge2Price: null,
  surcharge2Description: null,
  surcharge3Price: null,
  surcharge3Description: null,
}

const makeItem = (
  override: Pick<
    PriceChangeItem,
    "customerId" | "last12MonthsQty" | "freightTerms" | "shipToLocationId" | "billBack" | "previousBillBack"
  > & {
    customerName: string
  }
): PriceChangeItem => ({
  ...EMPTY_ITEM,
  ...override,
  customer: { name: override.customerName } as PriceChangeItem["customer"],
})

const makeRow = (item: PriceChangeItem, tier: Omit<PricingGridRow, "uom" | "item">): PricingGridRow => ({
  ...tier,
  uom: "",
  item,
})

const LIST_PRICE_ITEM = makeItem({
  customerId: "list-price",
  customerName: "List Price",
  shipToLocationId: "New York, NY",
  freightTerms: "PREPAID" as PriceChangeItem["freightTerms"],
  last12MonthsQty: 1200,
  billBack: 11,
  previousBillBack: 10.5,
})

const GLOBAL_CHEMICAL_ITEM = makeItem({
  customerId: "global-chemical",
  customerName: "Global Chemical",
  shipToLocationId: "Chicago, IL",
  freightTerms: "PREPAID" as PriceChangeItem["freightTerms"],
  last12MonthsQty: 3400,
  billBack: 9.5,
  previousBillBack: 9,
})

const POLYCHEM_INC_HOUSTON_ITEM = makeItem({
  customerId: "polychem-houston",
  customerName: "Polychem Inc",
  shipToLocationId: "Houston, TX",
  freightTerms: "PREPAID_AND_ADD" as PriceChangeItem["freightTerms"],
  last12MonthsQty: 800,
  billBack: 14.25,
  previousBillBack: null,
})

const POLYCHEM_INC_LA_ITEM = makeItem({
  customerId: "polychem-la",
  customerName: "Polychem Inc",
  shipToLocationId: "Los Angeles, CA",
  freightTerms: "PREPAID" as PriceChangeItem["freightTerms"],
  last12MonthsQty: 5600,
  billBack: 10.5,
  previousBillBack: 10,
})

const SUMMIT_POLYMERS_ITEM = makeItem({
  customerId: "summit-polymers",
  customerName: "Summit Polymers",
  shipToLocationId: "Atlanta, GA",
  freightTerms: "PREPAID" as PriceChangeItem["freightTerms"],
  last12MonthsQty: 420,
  billBack: 17.75,
  previousBillBack: null,
})

export const INITIAL_PRICING_EDIT_ROWS_MOCK: PricingGridRow[] = [
  makeRow(LIST_PRICE_ITEM, {
    id: "tier-1",
    qty: 55,
    price: 15.5,
    profit: 3.75,
    margin: 30,
    newPrice: 13,
    newProfit: 4.25,
    newMargin: 32.69,
    surcharge1Price: 1,
    surcharge1Description: "Desc 1",
    surcharge2Price: 2,
    surcharge2Description: "Desc 2",
    surcharge3Price: 3,
    surcharge3Description: "Desc 3",
  }),
  makeRow(LIST_PRICE_ITEM, {
    id: "tier-2",
    qty: 100,
    price: 12.5,
    profit: 3.75,
    margin: 30,
    newPrice: 13,
    newProfit: 4.25,
    newMargin: 32.69,
    surcharge1Price: 0.5,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
  makeRow(LIST_PRICE_ITEM, {
    id: "tier-3",
    qty: 500,
    price: 10.5,
    profit: 3.75,
    margin: 30,
    newPrice: 13,
    newProfit: 4.25,
    newMargin: 32.69,
    surcharge1Price: 0.5,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
  makeRow(GLOBAL_CHEMICAL_ITEM, {
    id: "tier-4",
    qty: 110,
    price: 9.8,
    profit: 1.96,
    margin: 20,
    newPrice: 10.5,
    newProfit: 2.63,
    newMargin: 25,
    surcharge1Price: 0.25,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
  makeRow(GLOBAL_CHEMICAL_ITEM, {
    id: "tier-5",
    qty: 1100,
    price: 90.8,
    profit: 1.96,
    margin: 20,
    newPrice: 10.5,
    newProfit: 2.63,
    newMargin: 25,
    surcharge1Price: 0.25,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
  makeRow(GLOBAL_CHEMICAL_ITEM, {
    id: "tier-6",
    qty: 11000,
    price: 900.8,
    profit: 1.96,
    margin: 20,
    newPrice: 10.5,
    newProfit: 2.63,
    newMargin: 25,
    surcharge1Price: 0.25,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
  makeRow(POLYCHEM_INC_HOUSTON_ITEM, {
    id: "tier-7",
    qty: 200,
    price: 15,
    profit: 1.2,
    margin: 8,
    newPrice: 16,
    newProfit: 2.08,
    newMargin: 13,
    surcharge1Price: 0.75,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
  makeRow(POLYCHEM_INC_LA_ITEM, {
    id: "tier-8",
    qty: 2200,
    price: 11.25,
    profit: 4.16,
    margin: 37,
    newPrice: 11.75,
    newProfit: 4.34,
    newMargin: 36.94,
    surcharge1Price: 1,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
  makeRow(SUMMIT_POLYMERS_ITEM, {
    id: "tier-9",
    qty: 150,
    price: 18.5,
    profit: 0.74,
    margin: 4,
    newPrice: 19,
    newProfit: 0.95,
    newMargin: 5,
    surcharge1Price: 0.6,
    surcharge1Description: null,
    surcharge2Price: null,
    surcharge2Description: null,
    surcharge3Price: null,
    surcharge3Description: null,
  }),
]
